import { action, computed, makeObservable, observable, runInAction } from 'mobx'
import type { ObservableMap } from 'mobx'
import type { DocumentData } from 'firebase/firestore'

import type {
  ObservableModel,
  ObservableModelClass,
  StaticModelCollection,
} from '../firestore-mobx/model'
import {
  getCatalogs,
  getCatalogsForUser,
  getMyCatalogs,
} from '../firestore/Catalog'
import { getSlideDecks, getSlideDecksForCatalog } from '../firestore/SlideDeck'
import { Catalog } from '../models/Catalog'
import type { FirebaseRepository } from '../models/FirebaseRepository'
import { SlideDeck } from '../models/SlideDeck'
import { Cubit } from './core'
import { getTeachingPlansForCatalog } from '../firestore/TeachingPlan'
import { TeachingPlan } from '../models/TeachingPlan'
import { TeachingPlanAuthor } from '../models/TeachingPlanAuthor'
import { getTeachingPlanAuthors } from '../firestore/TeachingPlanAuthor'
import { SlideDeckMaterial } from '../models/SlideDeckMaterial'
import { fetchSlideDeckMaterialsForInstructor } from '../firestore/SlideDeckMaterial'
import { filterSlideDeck } from '../util/filtering'
import { getTeachingPlanAggregation } from '../firestore/TeachingPlanAggregation'
import { TeachingPlanAggregation } from '../models/TeachingPlanAggregation'
import { SlideDeckAuthor } from '../models/SlideDeckAuthor'
import { getSlideDeckAuthors } from '../firestore/SlideDeckAuthor'
import { SlideDeckReference } from '../models/SlideDeckReference'
import { fetchSlideDeckReferences } from '../firestore/SlideDeckReference'
import { fetchSettingsProtectedCatalogIds } from '../firestore/SettingsCatalogs'
import { Section } from '../models/Section'
import { getSection } from '../firestore/Section'
import { convertApiDocsToModels } from '../firestore-mobx/apiModelHelper'
import {
  shouldUseV2SlideDeckRead,
  type SlideDeckReadSource,
} from './slideDeckReadSource'
import type { CatalogReadApi } from './catalogReadApi'
import { schema as catalogSchema } from '../firestore/Catalog/schema'
import { schema as slideDeckMaterialSchema } from '../firestore/SlideDeckMaterial/schema'
import { schema as slideDeckReferenceSchema } from '../firestore/SlideDeckReference/schema'
import { slideDeckAuthorSchema } from '../firestore/SlideDeckAuthor/schema'
import { schema as teachingPlanSchema } from '../firestore/TeachingPlan/schema'
import { teachingPlanAuthorSchema } from '../firestore/TeachingPlanAuthor/schema'
import { teachingPlanAggregationSchema } from '../firestore/TeachingPlanAggregation/schema'

export class InstructorLibraryWithTeachingPlansCubit extends Cubit {
  repository: FirebaseRepository

  catalogs: StaticModelCollection<Catalog>
  impersonatedCatalogs: StaticModelCollection<Catalog>
  section: Section
  sectionId?: string

  private _adminSlideDecks: StaticModelCollection<SlideDeck>

  private _slideDecksByCatalogId = observable.map<
    string,
    StaticModelCollection<SlideDeck>
  >()

  private _teachingPlansByCatalogId = observable.map<
    string,
    StaticModelCollection<TeachingPlan>
  >()

  teachingPlansAuthorsByPlanId = observable.map<
    string,
    StaticModelCollection<TeachingPlanAuthor>
  >()

  slideDeckAuthorsBySlideDeckId = observable.map<
    string,
    StaticModelCollection<SlideDeckAuthor>
  >()

  materialsBySlideDeckId = observable.map<
    string,
    StaticModelCollection<SlideDeckMaterial>
  >()

  referencesBySlideId = observable.map<
    string,
    StaticModelCollection<SlideDeckReference>
  >()

  private _isInternal: boolean

  // v2 (Adonis/Postgres) read path. When on, every deck/catalog/teaching-plan
  // read below goes through `_catalogApi` (injected from the page) instead of a
  // Firestore stream, and populates the same observables so the page renders
  // unchanged. Off = the existing Firestore path.
  private _useV2: boolean
  private _catalogApi?: CatalogReadApi
  @observable private _v2CatalogsLoading = false
  @observable private _v2AllDecksLoading = false
  private _v2FetchedCatalogIds = new Set<string>()
  private _v2FetchedAllDecks = false
  // Loads that failed. They are retried only on a user action (see
  // retryFailedV2Loads), never on re-render: the loaders run from getters, so
  // clearing the fetch guard on failure would refetch on every recompute and
  // loop on a persistent failure.
  private _v2FailedCatalogIds = new Set<string>()
  private _v2AllDecksFailed = false

  teachingPlanAggregationByTeachingPlanId = observable.map<
    string,
    TeachingPlanAggregation
  >()

  @observable searchFilter: string = ''
  @observable featuredFilter = false
  @observable selectedCatalogId: string | null = null
  @observable protected: Set<string> = new Set()

  @observable
  impersonatedUserId: string | undefined

  constructor(
    repository: FirebaseRepository,
    {
      catalogId: initialCatalogId,
      sectionId,
      impersonatedUserId,
      readSource,
      catalogApi,
    }: {
      sectionId?: string
      catalogId?: string
      impersonatedUserId?: string
      readSource?: SlideDeckReadSource
      catalogApi?: CatalogReadApi
    } = {}
  ) {
    super()
    makeObservable(this)

    this.repository = repository

    // Driven by the single app-wide read-source switch (SlideDeckSourceToggle).
    // A forced v2 without an injected api client falls back to v1 rather than
    // crashing, mirroring the student-facing #4935 guard.
    this._catalogApi = catalogApi
    this._useV2 = shouldUseV2SlideDeckRead(readSource) && !!catalogApi
    this.catalogs = Catalog.emptyCollection(repository)
    this.impersonatedCatalogs = Catalog.emptyCollection(repository)
    this._adminSlideDecks = SlideDeck.emptyCollection(repository)
    this.sectionId = sectionId
    this.section = Section.empty(repository)

    this._isInternal =
      repository.breakoutUser !== null && repository.breakoutUser.isInternal

    this.selectedCatalogId = initialCatalogId || null

    this.impersonatedUserId = impersonatedUserId

    if (this._isInternal) {
      fetchSettingsProtectedCatalogIds(repository).then((protectedIds) => {
        runInAction(() => {
          this.protected = new Set(protectedIds)
        })
      })
    }

    this.addReaction({
      whenThisChanges: () => this.currentCatalogId,
      thenRunThisCode: () => this.startCurrentCatalogStreams(),
      fireImmediately: true,
    })
  }

  @computed
  get currentCatalogViewDataLoading() {
    // v1 signals "view all" is active via the stream; v2 has no stream, so use
    // the in-flight flag instead. Without this the top-level spinner is skipped
    // on v2 and the grid renders empty until the fetch resolves.
    const internalSlideDecksLoading = this._useV2
      ? this._v2AllDecksLoading
      : this.hasStream('internal-slide-decks') &&
        this._adminSlideDecks.isLoading

    const teachingPlansForCatalogColl = this._teachingPlansByCatalogId.get(
      this.currentCatalogId || ''
    )
    const slideDecksForCatalogColl = this._slideDecksByCatalogId.get(
      this.currentCatalogId || ''
    )

    // if no stream results or empty (loading) results, still loading
    const loadingTeachingPlans = teachingPlansForCatalogColl?.isLoading ?? true
    const loadingSlideDecks = slideDecksForCatalogColl?.isLoading ?? true

    return (
      internalSlideDecksLoading ||
      (this.currentCatalogId && (loadingTeachingPlans || loadingSlideDecks))
    )
  }

  @computed
  get catalogsLoading() {
    // Impersonation still streams from Firestore even on v2, so keep waiting on
    // the impersonated set in both paths.
    const impersonatedLoading =
      !!this.impersonatedUserId && this.impersonatedCatalogs.isLoading
    if (this._useV2) return this._v2CatalogsLoading || impersonatedLoading
    return this.catalogs.isLoading || impersonatedLoading
  }

  @computed
  get selectedCatalogIdIsInvalid(): boolean {
    if (!this.selectedCatalogId) return false
    if (this.catalogsLoading) return false
    return !this.catalogs.models.some(
      (catalog) => catalog.id === this.selectedCatalogId
    )
  }

  @action
  setSelectedCatalogId(catalogId: string | null) {
    this.selectedCatalogId = catalogId
    this.retryFailedV2Loads()
  }

  // Re-run v2 loads that failed, so a transient error doesn't pin a catalog or
  // the "view all" grid to empty until reload. Called on user actions (catalog
  // selection, the "view all" toggle). No-op on v1 and when nothing failed.
  @action
  retryFailedV2Loads() {
    if (!this._useV2) return
    const retryCatalogs = this._v2FailedCatalogIds.size > 0
    for (const catalogId of this._v2FailedCatalogIds) {
      this._v2FetchedCatalogIds.delete(catalogId)
      // Drop the loaded-but-empty collections so the page spins while the
      // retry is in flight.
      this._slideDecksByCatalogId.delete(catalogId)
      this._teachingPlansByCatalogId.delete(catalogId)
    }
    this._v2FailedCatalogIds.clear()
    if (this._v2AllDecksFailed) {
      this._v2AllDecksFailed = false
      // The next adminSlideDecks read fetches again.
      this._v2FetchedAllDecks = false
    }
    if (retryCatalogs) this.startCurrentCatalogStreams()
  }

  @computed
  get currentCatalogId() {
    if (this.selectedCatalogId) return this.selectedCatalogId

    // if no catalog is selected, default to:
    if (this.catalogs.models.length > 0) {
      if (this._isInternal && this.protected.size > 0) {
        const firstProtectedCatalog = this.catalogs.models.find((catalog) =>
          this.protected.has(catalog.id)
        )
        if (firstProtectedCatalog) {
          return firstProtectedCatalog.id
        }
      }

      return this.catalogs.models[0].id
    }

    return undefined
  }

  @computed
  get slideDecksForCurrentCatalog() {
    if (!this.currentCatalogId) return []
    this.startCurrentCatalogStreams()

    return this.slideDecksByCatalogId[this.currentCatalogId] || []
  }

  @computed
  get teachingPlansForCurrentCatalog() {
    if (!this.currentCatalogId) return []
    this.startCurrentCatalogStreams()

    return this.teachingPlansByCatalogId[this.currentCatalogId] || []
  }

  @computed
  get slideDecksByCatalogId() {
    const applySearchFilter = (slideDecks: SlideDeck[]) => {
      if (!this.searchFilter) return slideDecks
      return slideDecks.filter((slideDeck) =>
        filterSlideDeck(this.searchFilter, slideDeck)
      )
    }

    const applyFeaturedFilter = (slideDecks: SlideDeck[]) => {
      if (!this.featuredFilter) return slideDecks
      return slideDecks.filter((slideDeck) => slideDeck.data.slideDeckFeatured)
    }

    const applyFilters = (
      slideDecks: SlideDeck[],
      filters: ((slideDecks: SlideDeck[]) => SlideDeck[])[]
    ) => {
      return filters.reduce((decks, filter) => filter(decks), slideDecks)
    }

    // convert to a plain object and apply search filter if present
    const slideDecksByCatalog = Object.fromEntries(
      Array.from(this._slideDecksByCatalogId.entries()).map(([key, value]) => {
        return [
          key,
          applyFilters(value.models, [applySearchFilter, applyFeaturedFilter]),
        ]
      })
    )
    return slideDecksByCatalog
  }

  @computed
  get teachingPlansByCatalogId() {
    // convert to a plain object
    return Object.fromEntries(
      Array.from(this._teachingPlansByCatalogId.entries()).map(
        ([key, value]) => [key, value.models]
      )
    )
  }

  @computed
  get slideDecks() {
    if (!this._isInternal) {
      const soFar = new Set()
      const slideDecks = Array.from(this._slideDecksByCatalogId.values())
        .map((collection) => collection.models)
        .flat()
      // don't allow duplicate slide decks
      return slideDecks.filter((slideDeck) => {
        if (soFar.has(slideDeck.id)) return false
        soFar.add(slideDeck.id)
        return true
      })
    }
    return this._adminSlideDecks.models
  }

  initialize(): void {
    if (this._useV2) {
      this.loadV2Catalogs()
      // Impersonation and section context stay on Firestore in v2 (rarely used,
      // orthogonal to the deck/teaching-plan read path).
      if (this.sectionId) {
        this.addStream(
          getSection(this.repository, { sectionId: this.sectionId }),
          (section) => this.section.replaceModel(section),
          { name: 'section' }
        )
      }
      this.restartImpersonatedCatalogsStream()
      return
    }
    // if user is an admin call getCatalogs
    // otherwise call getMyCatalogs
    const isInternal = this.repository.breakoutUser?.isInternal
    this.addStream(
      isInternal
        ? getCatalogs(this.repository)
        : getMyCatalogs(this.repository),
      (catalogs: Catalog[]) => {
        this.catalogs.replaceModels(
          catalogs.sort((a, b) =>
            a.data.catalogName.localeCompare(b.data.catalogName)
          )
        )
      },
      {
        name: 'catalogs',
      }
    )

    if (this.sectionId) {
      this.addStream(
        getSection(this.repository, { sectionId: this.sectionId }),
        (section) => {
          this.section.replaceModel(section)
        },
        {
          name: 'section',
        }
      )
    }

    this.restartImpersonatedCatalogsStream()
  }

  startAdminSlideDeckStream() {
    if (this._useV2) {
      this.loadV2AllDecks()
      return
    }
    if (this.hasStream('internal-slide-decks')) return
    this.addStream(
      getSlideDecks(this.repository),
      (slideDecks) => {
        this._adminSlideDecks.replaceModels(slideDecks)
      },
      {
        name: 'internal-slide-decks',
      }
    )
  }

  startCurrentCatalogStreams() {
    if (!this.currentCatalogId) return

    if (this._useV2) {
      this.loadV2CatalogData(this.currentCatalogId)
      return
    }

    this.addSlideDeckStreams(this.currentCatalogId)
    this.addTeachingPlanStreams(this.currentCatalogId)
  }

  // --- v2 (Adonis) loaders ---------------------------------------------------

  private async loadV2Catalogs() {
    const api = this._catalogApi
    if (!api) return
    runInAction(() => {
      this._v2CatalogsLoading = true
    })
    try {
      const docs = await api.fetchCatalogs()
      if (this.isDisposed) return
      const catalogs = convertApiDocsToModels(
        this.repository,
        docs.map((d) => ({ id: d.id, data: catalogSchema.parse(d.data) })),
        Catalog
      )
      runInAction(() => {
        this.catalogs.replaceModels(
          catalogs.sort((a, b) =>
            a.data.catalogName.localeCompare(b.data.catalogName)
          )
        )
      })
    } catch (err) {
      // Recover to a loaded-but-empty state (matching the v1 stream's
      // console.error-and-carry-on behaviour) so the page stops spinning
      // instead of hanging on the failure.
      if (!this.isDisposed) {
        runInAction(() => {
          this.catalogs.replaceModels([])
        })
      }
      console.error('loadV2Catalogs failed', err)
    } finally {
      if (!this.isDisposed) {
        runInAction(() => {
          this._v2CatalogsLoading = false
        })
      }
    }
  }

  private async loadV2CatalogData(catalogId: string) {
    const api = this._catalogApi
    if (!api) return
    if (this._v2FetchedCatalogIds.has(catalogId)) return
    this._v2FetchedCatalogIds.add(catalogId)

    try {
      const [deckDocs, planDocs] = await Promise.all([
        api.fetchDecks(catalogId),
        api.fetchTeachingPlans(catalogId),
      ])
      if (this.isDisposed) return

      // Build decks through the SlideDeck schema so `data.updatedAt` (and other
      // coerced fields) land as real types, not raw JSON strings.
      const slideDecks = deckDocs.map((d) =>
        SlideDeck.buildModelFromApiResponse(this.repository, {
          id: d.id,
          data: d.data,
        })
      )
      const teachingPlans = convertApiDocsToModels(
        this.repository,
        planDocs.map((p) => ({
          id: p.id,
          data: teachingPlanSchema.parse(p.data),
        })),
        TeachingPlan
      )

      runInAction(() => {
        this.setCollection(
          this._slideDecksByCatalogId,
          catalogId,
          slideDecks,
          SlideDeck
        )
        this.setCollection(
          this._teachingPlansByCatalogId,
          catalogId,
          teachingPlans,
          TeachingPlan
        )

        // Per-deck subcollections, embedded in the deck response.
        deckDocs.forEach((deck) => {
          this.materialsBySlideDeckId.set(
            deck.id,
            this.buildCollection(
              deck.materials,
              slideDeckMaterialSchema,
              SlideDeckMaterial
            )
          )
          this.referencesBySlideId.set(
            deck.id,
            this.buildCollection(
              deck.references,
              slideDeckReferenceSchema,
              SlideDeckReference
            )
          )
          this.slideDeckAuthorsBySlideDeckId.set(
            deck.id,
            this.buildCollection(
              deck.authors,
              slideDeckAuthorSchema,
              SlideDeckAuthor
            )
          )
        })

        // Teaching-plan authors and the derived aggregation counts.
        planDocs.forEach((plan) => {
          this.teachingPlansAuthorsByPlanId.set(
            plan.id,
            this.buildCollection(
              plan.authors,
              teachingPlanAuthorSchema,
              TeachingPlanAuthor
            )
          )
          const [aggregation] = convertApiDocsToModels(
            this.repository,
            [
              {
                id: plan.id,
                data: teachingPlanAggregationSchema.parse({
                  catalogId,
                  teachingPlanId: plan.id,
                  moduleCount: plan.aggregation.moduleCount,
                  slideDeckCount: plan.aggregation.slideDeckCount,
                }),
              },
            ],
            TeachingPlanAggregation
          )
          this.teachingPlanAggregationByTeachingPlanId.set(plan.id, aggregation)
        })
      })
    } catch (err) {
      // Recover to a loaded-but-empty catalog so `currentCatalogViewDataLoading`
      // resolves (the loading getters read `collection?.isLoading ?? true`, so a
      // missing collection would spin forever). Keep the fetch guard set so a
      // persistent failure does not re-fetch on every re-render; record the
      // failure so retryFailedV2Loads can re-run it on a user action.
      if (!this.isDisposed) {
        runInAction(() => {
          this._v2FailedCatalogIds.add(catalogId)
          this.setCollection(
            this._slideDecksByCatalogId,
            catalogId,
            [],
            SlideDeck
          )
          this.setCollection(
            this._teachingPlansByCatalogId,
            catalogId,
            [],
            TeachingPlan
          )
        })
      }
      console.error('loadV2CatalogData failed', err)
    }
  }

  private async loadV2AllDecks() {
    const api = this._catalogApi
    if (!api || this._v2FetchedAllDecks) return
    this._v2FetchedAllDecks = true
    runInAction(() => {
      this._v2AllDecksLoading = true
    })
    try {
      const deckDocs = await api.fetchDecks()
      if (this.isDisposed) return
      const slideDecks = deckDocs.map((d) =>
        SlideDeck.buildModelFromApiResponse(this.repository, {
          id: d.id,
          data: d.data,
        })
      )
      runInAction(() => {
        this._adminSlideDecks.replaceModels(slideDecks)
      })
    } catch (err) {
      // Mark the collection loaded-but-empty so the admin "view all" grid
      // renders an empty state instead of appearing to still be loading. Keep
      // the guard set to avoid re-fetching on every re-render; record the
      // failure so retryFailedV2Loads can re-run it on a user action.
      if (!this.isDisposed) {
        runInAction(() => {
          this._v2AllDecksFailed = true
          this._adminSlideDecks.replaceModels([])
        })
      }
      console.error('loadV2AllDecks failed', err)
    } finally {
      if (!this.isDisposed) {
        runInAction(() => {
          this._v2AllDecksLoading = false
        })
      }
    }
  }

  private setCollection<M extends ObservableModel<DocumentData>>(
    map: ObservableMap<string, StaticModelCollection<M>>,
    key: string,
    models: M[],
    modelClass: {
      emptyCollection: (r: FirebaseRepository) => StaticModelCollection<M>
    }
  ) {
    const found = map.get(key)
    if (found) {
      found.replaceModels(models)
    } else {
      const collection = modelClass.emptyCollection(this.repository)
      collection.replaceModels(models)
      map.set(key, collection)
    }
  }

  // Builds a collection from API docs, running each doc through the model's zod
  // schema first so timestamp/enum fields are coerced (e.g. a reference's
  // `publishedAt` becomes a Date, not the raw `{ seconds, nanoseconds }`).
  // `convertApiDocsToModels` alone skips the schema, so raw JSON would leak to
  // the client and any `.getTime()`/`DateTime.fromJSDate(...)` getter would throw.
  private buildCollection<T extends DocumentData, M extends ObservableModel<T>>(
    docs: { id: string; data: unknown }[],
    schema: { parse(value: unknown): T },
    modelClass: ObservableModelClass<T, M> & {
      emptyCollection: (r: FirebaseRepository) => StaticModelCollection<M>
    }
  ): StaticModelCollection<M> {
    const models = convertApiDocsToModels(
      this.repository,
      docs.map((d) => ({ id: d.id, data: schema.parse(d.data) })),
      modelClass
    )
    const collection = modelClass.emptyCollection(this.repository)
    collection.replaceModels(models)
    return collection
  }

  get adminSlideDecks() {
    this.startAdminSlideDeckStream()

    return this._adminSlideDecks
  }

  @computed
  get hasSearchFilter() {
    return this.searchFilter.length > 0
  }

  @action
  setSearchFilter(filter: string) {
    this.searchFilter = filter
  }

  @action
  setFeaturedFilter(value: boolean) {
    this.featuredFilter = value
  }

  addSlideDeckStreams(catalogId: string) {
    const streamName = `slide-decks-${catalogId}`

    if (this.hasStream(streamName)) return

    this.addStream(
      getSlideDecksForCatalog(this.repository, {
        catalogId: catalogId,
      }),
      (slideDecks) => {
        const found = this._slideDecksByCatalogId.get(catalogId)
        if (found) {
          found.replaceModels(slideDecks)
        } else {
          const collection = SlideDeck.emptyCollection(this.repository)
          collection.replaceModels(slideDecks)
          runInAction(() => {
            this._slideDecksByCatalogId.set(catalogId, collection)
          })
        }
        this.fetchMaterials(slideDecks)
        this.fetchReferences(slideDecks)
        this.getSlideDeckAuthors(slideDecks)
      },
      {
        name: streamName,
        onError: (err) => {
          console.error(err)
        },
      }
    )
  }

  addTeachingPlanStreams(catalogId: string) {
    const streamName = `teaching-plans-${catalogId}`

    if (this.hasStream(streamName)) return

    this.addStream(
      getTeachingPlansForCatalog(this.repository, {
        catalogId: catalogId,
      }),
      (teachingPlans) => {
        const found = this._teachingPlansByCatalogId.get(catalogId)

        if (found) {
          found.replaceModels(teachingPlans)
        } else {
          const collection = TeachingPlan.emptyCollection(this.repository)
          collection.replaceModels(teachingPlans)
          runInAction(() => {
            this._teachingPlansByCatalogId.set(catalogId, collection)
          })
        }
        this.getTeachingPlanAggregations(teachingPlans)
        this.getTeachingPlanAuthors(teachingPlans)
      },
      {
        name: streamName,
      }
    )
  }

  getTeachingPlanAggregations(teachingPlans: TeachingPlan[]) {
    const formatStreamName = (teachingPlanId: string) =>
      `teachingPlanAggregation-${teachingPlanId}`

    // Setup a stream for each aggregation.
    teachingPlans.forEach(async (teachingPlan) => {
      const streamName = formatStreamName(teachingPlan.id)
      if (this.hasStream(streamName)) {
        return
      }

      this.addStream(
        getTeachingPlanAggregation(this.repository, {
          catalogId: teachingPlan.data.catalogId,
          teachingPlanId: teachingPlan.id,
        }),
        (aggregation) => {
          runInAction(() => {
            this.teachingPlanAggregationByTeachingPlanId.set(
              teachingPlan.id,
              aggregation
            )
          })
        },
        { name: streamName, onError: (err) => console.error(err) }
      )
    })
  }

  fetchMaterials(slideDecks: SlideDeck[]) {
    slideDecks.forEach(async (slideDeck) => {
      const id = slideDeck.id

      const found = this.materialsBySlideDeckId.get(id)
      // if we already have the materials, don't fetch them again
      if (found) return

      const materials = await fetchSlideDeckMaterialsForInstructor(
        this.repository,
        {
          slideDeckId: id,
        }
      )

      const collection = SlideDeckMaterial.emptyCollection(this.repository)
      collection.replaceModels(materials)
      runInAction(() => {
        this.materialsBySlideDeckId.set(id, collection)
      })
    })
  }

  fetchReferences(slideDecks: SlideDeck[]) {
    slideDecks.forEach(async (slideDeck) => {
      const id = slideDeck.id

      const found = this.referencesBySlideId.get(id)
      // if we already have the references, don't fetch them again
      if (found) return

      const references = await fetchSlideDeckReferences(this.repository, {
        slideDeckId: id,
      })

      const collection = SlideDeckReference.emptyCollection(this.repository)
      collection.replaceModels(references)
      runInAction(() => {
        this.referencesBySlideId.set(id, collection)
      })
    })
  }

  getTeachingPlanAuthors(teachingPlans: TeachingPlan[]) {
    teachingPlans.forEach(async (teachingPlan) => {
      const id = teachingPlan.id
      const catalogId = teachingPlan.data.catalogId
      const streamName = `teaching-plan-authors-${id}`
      if (this.hasStream(streamName)) return

      this.addStream(
        getTeachingPlanAuthors(this.repository, {
          catalogId,
          teachingPlanId: id,
        }),
        (authors) => {
          const found = this.teachingPlansAuthorsByPlanId.get(id)

          if (found) {
            found.replaceModels(authors)
          } else {
            const collection = TeachingPlanAuthor.emptyCollection(
              this.repository
            )
            collection.replaceModels(authors)
            runInAction(() => {
              this.teachingPlansAuthorsByPlanId.set(id, collection)
            })
          }
        },
        {
          name: streamName,
        }
      )
    })
  }

  getSlideDeckAuthors(slideDecks: SlideDeck[]) {
    slideDecks.forEach(async (slideDeck) => {
      const id = slideDeck.id
      const streamName = `slide-deck-authors-${id}`
      if (this.hasStream(streamName)) return

      this.addStream(
        getSlideDeckAuthors(this.repository, {
          slideDeckId: id,
        }),
        (authors) => {
          const found = this.slideDeckAuthorsBySlideDeckId.get(id)

          if (found) {
            found.replaceModels(authors)
          } else {
            const collection = SlideDeckAuthor.emptyCollection(this.repository)
            collection.replaceModels(authors)
            runInAction(() => {
              this.slideDeckAuthorsBySlideDeckId.set(id, collection)
            })
          }
        },
        {
          name: streamName,
        }
      )
    })
  }

  @action
  setImpersonatedUserId(userId: string | undefined) {
    this.impersonatedUserId = userId
    this.restartImpersonatedCatalogsStream()
  }

  restartImpersonatedCatalogsStream() {
    this.removeStream('impersonated-catalogs')
    if (!this.impersonatedUserId) return

    this.addStream(
      getCatalogsForUser(this.repository, {
        userId: this.impersonatedUserId,
      }),
      (results) => {
        const catalogs = results.map((r) => r.catalog)
        this.impersonatedCatalogs.replaceModels(
          catalogs.sort((a, b) =>
            a.data.catalogName.localeCompare(b.data.catalogName)
          )
        )
      },
      {
        name: 'impersonated-catalogs',
      }
    )
  }
}
